import React, { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AppProviders } from './providers/AppProviders';
import {
  ProtectedRoute,
  PublicRoute,
} from './components/auth';
import { AuthNotificationWrapper } from './components/auth';
import { MainLayout } from './components/layout';
import {
  ErrorBoundary,
  NetworkStatus,
  LoadingSpinner,
} from './components/common';
import { ToastContainer } from './components/notifications';
import { useOfflineSupport } from './hooks/useOfflineSupport';
import { usePerformanceOptimization } from './hooks/usePerformanceOptimization';
import { ErrorHandler } from './utils/errorHandler';

// Lazy load pages for code splitting
const LoginPage = React.lazy(() =>
  import('./pages/auth/LoginPage').then((module) => ({
    default: module.LoginPage,
  }))
);
const DashboardHome = React.lazy(() =>
  import('./pages/dashboard/DashboardHome').then((module) => ({
    default: module.DashboardHome,
  }))
);
const HolidaysPage = React.lazy(() =>
  import('./pages/holidays/HolidaysPage').then((module) => ({
    default: module.HolidaysPage,
  }))
);
const PendingHolidaysPage = React.lazy(() =>
  import('./pages/holidays/PendingHolidaysPage').then((module) => ({
    default: module.PendingHolidaysPage,
  }))
);
const UsersPage = React.lazy(() =>
  import('./pages/users/UsersPage').then((module) => ({
    default: module.UsersPage,
  }))
);
const ReportsPage = React.lazy(() =>
  import('./pages/reports/ReportsPage').then((module) => ({
    default: module.ReportsPage,
  }))
);
const AnnualLeaveRecordsPage = React.lazy(() =>
  import('./pages/reports/AnnualLeaveRecordsPage').then((module) => ({
    default: module.AnnualLeaveRecordsPage,
  }))
);
const SettingsPage = React.lazy(() =>
  import('./pages/settings/SettingsPage').then((module) => ({
    default: module.SettingsPage,
  }))
);

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-sm text-gray-600">Loading page...</p>
    </div>
  </div>
);

// App content component that uses hooks (must be inside providers)
function AppContent() {
  // Initialize offline support
  useOfflineSupport();

  // Initialize performance optimizations
  usePerformanceOptimization({
    enableMemoryMonitoring: true,
    enableCoreWebVitals: true,
    memoryCleanupInterval: 30000,
    enableResourceTiming: true,
  });

  return (
    <AuthNotificationWrapper>
      <NetworkStatus
        onStatusChange={(isOnline) => {
          if (isOnline) {
            console.log('🌐 Network connection restored');
          } else {
            console.log('📵 Network connection lost');
          }
        }}
      />
      <Router>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute restricted>
                <Suspense fallback={<PageLoadingFallback />}>
                  <LoginPage />
                </Suspense>
              </PublicRoute>
            }
          />

          {/* Protected routes with layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard routes */}
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route
              path="dashboard"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <DashboardHome />
                </Suspense>
              }
            />

            {/* Holiday management routes */}
            <Route
              path="holidays"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <HolidaysPage />
                </Suspense>
              }
            />
            <Route
              path="holidays/pending"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <PendingHolidaysPage />
                </Suspense>
              }
            />

            {/* User management routes */}
            <Route
              path="users"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <UsersPage />
                </Suspense>
              }
            />

            {/* Reports routes */}
            <Route
              path="reports"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <ReportsPage />
                </Suspense>
              }
            />
            <Route
              path="reports/annual-leave-records"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <AnnualLeaveRecordsPage />
                </Suspense>
              }
            />

            {/* Settings routes */}
            <Route
              path="settings"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <SettingsPage />
                </Suspense>
              }
            />
          </Route>

          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <ToastContainer />
      </Router>
    </AuthNotificationWrapper>
  );
}

function App() {
  const handleGlobalError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('🚨 Global error caught by ErrorBoundary:', error, errorInfo);
    // Log error to our error handler
    ErrorHandler.logError(ErrorHandler.handleApiError(error), {
      type: 'react_error',
      componentStack: errorInfo.componentStack,
    });
  };

  return (
    <ErrorBoundary onError={handleGlobalError} resetOnPropsChange={true}>
      <AppProviders>
        <AppContent />
      </AppProviders>
    </ErrorBoundary>
  );
}

export default App;
